import { PlusOutlined } from '@ant-design/icons';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import { Image, Upload, message } from 'antd';
import { useRef, useState } from 'react';
import { UploadBusinessType } from '../../types';
import {
  normalizeBatchUploadItems,
  postImageFilesBatch,
} from '../../utils/postImageFilesBatch';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

type UploadRequestOption = Parameters<
  NonNullable<UploadProps['customRequest']>
>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (e) => reject(e);
  });

const defaultBeforeUpload = (file: FileType) => {
  const isValidType =
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/bmp';
  if (!isValidType) {
    message.error('You can only upload JPG/PNG/BMP file!');
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error('Image must smaller than 2MB!');
  }
  return isValidType && isLt2M;
};

const withServerPreview = (file: UploadFile): UploadFile => {
  if (file.status !== 'done' || !file.response?.data?.[0]) {
    return file;
  }
  const item = file.response.data[0] as {
    fileUrl?: string;
    url?: string;
  };
  const url = item.fileUrl || item.url;
  return url ? { ...file, url, thumbUrl: url } : file;
};

export type MultiImageUploadProps = {
  businessType: UploadBusinessType;
  value?: UploadFile[];
  onChange?: (fileList: UploadFile[]) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  multiple?: boolean;
  disabled?: boolean;
  beforeUpload?: UploadProps['beforeUpload'];
};

export function isUploadFileListUploading(fileList: UploadFile[]): boolean {
  return fileList.some((f) => f.status === 'uploading');
}

export const MultiImageUpload = ({
  businessType,
  value,
  onChange,
  onUploadingChange,
  multiple = true,
  disabled,
  beforeUpload = defaultBeforeUpload,
}: MultiImageUploadProps) => {
  const [internalList, setInternalList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const batchQueueRef = useRef<UploadRequestOption[]>([]);
  const flushScheduledRef = useRef(false);
  const controlled = value !== undefined;
  const fileList = controlled ? value : internalList;

  const runBatchUpload = async (queue: UploadRequestOption[]) => {
    const files = queue.map((o) => o.file as FileType);
    try {
      const responseBody = await postImageFilesBatch(files, businessType);
      const items = normalizeBatchUploadItems(responseBody);
      const base =
        responseBody && typeof responseBody === 'object'
          ? (responseBody as Record<string, unknown>)
          : {};

      queue.forEach((opt, i) => {
        const item = items[i];
        if (item == null) {
          const err = new Error('Batch response missing entry for this file.');
          opt.onError?.(err);
          message.error(err.message);
          return;
        }
        opt.onSuccess?.(
          { ...base, data: [item] } as object,
          new XMLHttpRequest()
        );
      });
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Batch upload failed.');
      queue.forEach((opt) => {
        opt.onError?.(error);
      });
      message.error(error.message);
    }
  };

  const scheduleBatchFlush = () => {
    if (flushScheduledRef.current) return;
    flushScheduledRef.current = true;
    queueMicrotask(() => {
      flushScheduledRef.current = false;
      const queue = batchQueueRef.current;
      batchQueueRef.current = [];
      if (queue.length > 0) {
        runBatchUpload(queue).catch(() => undefined);
      }
    });
  };

  const customRequest: UploadProps['customRequest'] = (options) => {
    batchQueueRef.current.push(options);
    scheduleBatchFlush();
  };

  const handlePreview: UploadProps['onPreview'] = async (file) => {
    let src = file.url ?? file.thumbUrl;
    if (!src && file.preview) {
      src = typeof file.preview === 'string' ? file.preview : undefined;
    }
    if (!src && file.originFileObj) {
      try {
        src = await getBase64(file.originFileObj as FileType);
      } catch {
        message.error('Could not preview this image.');
        return;
      }
    }
    if (!src) {
      message.warning('No preview available for this file.');
      return;
    }
    setPreviewSrc(src);
    setPreviewOpen(true);
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    const next = info.fileList.map(withServerPreview);
    if (!controlled) {
      setInternalList(next);
    }
    onChange?.(next);
    onUploadingChange?.(isUploadFileListUploading(next));
  };

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const atMax = !multiple && fileList.length >= 1;
  const isSingleImage = !multiple;

  const itemStyle = isSingleImage
    ? { width: 204, height: 156, objectFit: 'cover' as const }
    : undefined;

  const listItemClassName = isSingleImage ? 'single-image-upload' : undefined;

  return (
    <>
      {isSingleImage && (
        <style>
          {`
            .single-image-upload .ant-upload-list-item-container {
              width: 204px !important;
              height: 156px !important;
              margin: 0 !important;
            }
            .single-image-upload .ant-upload-list-item {
              width: 204px !important;
              height: 156px !important;
              margin: 0 !important;
            }
            .single-image-upload .ant-upload-list-item img {
              width: 204px !important;
              height: 156px !important;
              object-fit: cover !important;
            }
            .single-image-upload .ant-upload-select {
              width: 204px !important;
              height: 156px !important;
              margin: 0 !important;
            }
          `}
        </style>
      )}
      <Upload
        className={listItemClassName}
        multiple={multiple}
        name="files"
        listType="picture-card"
        accept="image/jpeg,image/png,image/bmp"
        disabled={disabled}
        fileList={fileList}
        customRequest={customRequest}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        onPreview={handlePreview}
        itemRender={(originNode, file) => {
          if (isSingleImage) {
            return (
              <div
                style={{
                  width: 204,
                  height: 156,
                  overflow: 'hidden',
                  borderRadius: 8,
                }}
              >
                {originNode}
              </div>
            );
          }
          return originNode;
        }}
      >
        {atMax ? null : uploadButton}
      </Upload>
      <Image
        wrapperStyle={{ display: 'none' }}
        src={previewSrc || undefined}
        preview={{
          visible: previewOpen && !!previewSrc,
          onVisibleChange: (open) => {
            setPreviewOpen(open);
          },
          afterOpenChange: (open) => {
            if (!open) {
              setPreviewSrc('');
            }
          },
        }}
      />
    </>
  );
};

export default MultiImageUpload;
